import { runAz } from "./az.js";

/** One model offering in a region's Foundry catalog, normalized. */
export interface CatalogRow {
  /** Deployment format — "OpenAI", "xAI", "Anthropic", … Required by `az … deployment create --model-format`. */
  format: string;
  name: string;
  version: string;
  /** Deduped SKU names, e.g. ["DataZoneStandard", "GlobalStandard"]. */
  skus: string[];
  /** capabilities.agentsV2 === "true" — the live signal for "can back a Foundry agent". */
  agentsV2: boolean;
}

/**
 * Discriminated so a probe FAILURE is distinguishable from a model being ABSENT.
 * Deliberately unlike `listModelQuota`, which flattens errors to []: an empty
 * quota list keeps rungs as candidates (safe), but an empty catalog would
 * falsely mark every rung "not-in-catalog" and let the intake persona claim a
 * quota reason it never observed.
 */
export type CatalogResult =
  | { ok: true; rows: CatalogRow[] }
  | { ok: false; error: "denied" | "unavailable" | "malformed" };

interface AzModelEntry {
  model?: {
    format?: string;
    name?: string;
    version?: string;
    skus?: { name?: string }[];
    capabilities?: Record<string, string>;
  };
}

/** List the region's model catalog. Never throws — every failure is a typed result. */
export async function listAgentModels(region: string): Promise<CatalogResult> {
  let out: string;
  try {
    out = await runAz(["cognitiveservices", "model", "list", "-l", region, "--output", "json"]);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    // Match the az stderr, not the whole runAz message. Permission failures must
    // stay distinguishable from transient ones: only the former is a stable
    // property of this founder's account.
    const denied = /AuthorizationFailed|Forbidden|\b403\b|does not have authorization/i.test(msg);
    return { ok: false, error: denied ? "denied" : "unavailable" };
  }

  let raw: unknown;
  try {
    raw = JSON.parse(out);
  } catch {
    return { ok: false, error: "malformed" };
  }
  if (!Array.isArray(raw) || raw.some((e) => typeof e !== "object" || e === null)) {
    return { ok: false, error: "malformed" };
  }

  try {
    const rows = (raw as AzModelEntry[]).map((e) => ({
      format: e.model?.format ?? "",
      name: e.model?.name ?? "",
      version: e.model?.version ?? "",
      skus: [...new Set((e.model?.skus ?? []).map((s) => s.name ?? "").filter(Boolean))],
      agentsV2: e.model?.capabilities?.agentsV2 === "true",
    }));
    return { ok: true, rows };
  } catch {
    // Any shape we did not anticipate inside a row is malformed input, not a crash.
    // The two guards are complementary: the element check above catches junk that
    // does NOT throw (a string entry yields undefined, not an exception), and this
    // catch covers nested shapes that do.
    return { ok: false, error: "malformed" };
  }
}
